import React, { useEffect, useRef } from 'react';

/**
 * The phone's container. Everything that is a column on a desktop — the app's
 * navigation, the wizard's journey, Robbie's rail — becomes one of these when
 * there is no room for a column, and nothing else in the product changes shape.
 *
 * Two sides, one behaviour:
 *   - `left` for navigation, because that is where the rail it replaces lives,
 *     and because a thumb reaching for a menu expects the panel to come from
 *     the edge the button sits on.
 *   - `bottom` for content and conversation, which is the reachable half of a
 *     phone screen. It stops short of the top so the page behind stays
 *     visible: a sheet that covers everything is a page, and a page needs a
 *     back button rather than a backdrop.
 *
 * The body is locked while one is open, so a flick inside the sheet cannot
 * scroll the page underneath it and leave the user somewhere they did not go.
 *
 * `keepMounted` is for a sheet whose contents are alive whether or not anybody
 * is looking at them — Robbie's panel holds a live conversation, and throwing
 * it away every time the sheet closes would reload the thread on every peek.
 */
export interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** Which edge it comes from. Default `bottom`. */
  side?: 'bottom' | 'left';
  /** Names the dialog for a screen reader. */
  label: string;
  /**
   * Keeps the children mounted (hidden) while closed. Off by default: opening
   * a sheet fresh is usually the reset you want.
   */
  keepMounted?: boolean;
  /** Applied to the panel — height for a bottom sheet, width for a drawer. */
  className?: string;
  children: React.ReactNode;
}

const PANEL_BY_SIDE = {
  bottom:
    'inset-x-0 bottom-0 max-h-[88vh] w-full rounded-t-panel border-t border-line motion-safe:animate-sheet-up',
  left: 'inset-y-0 left-0 h-full w-[84vw] max-w-[300px] motion-safe:animate-sheet-left',
} as const;

export const Sheet: React.FC<SheetProps> = ({
  open,
  onClose,
  side = 'bottom',
  label,
  keepMounted = false,
  className = '',
  children,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    // A phone keyboard is a real keyboard, and a Bluetooth one even more so.
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  // Lock the page behind it. Restoring the previous value rather than clearing
  // it means two overlapping sheets cannot unlock the page between them.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  if (!open && !keepMounted) return null;

  return (
    <div className={`fixed inset-0 z-50 ${open ? '' : 'hidden'}`}>
      <div
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 bg-chrome/50 motion-safe:animate-sheet-fade"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className={`absolute flex flex-col overflow-hidden bg-surface shadow-floating focus:outline-none ${PANEL_BY_SIDE[side]} ${className}`}
      >
        {children}
      </div>
    </div>
  );
};

export default Sheet;
